import { useHttpClient } from '@repo/api-client/react';
import {
  AiConversationDetailSchema,
  AiConversationListResponseSchema,
  type AiConversationDetail,
  type AiConversationSummary,
} from '@repo/api-client/dtos';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const aiKeys = {
  conversations: (orgId: string) => ['ai', orgId, 'conversations'] as const,
  conversation: (orgId: string, id: string) =>
    ['ai', orgId, 'conversation', id] as const,
};

export { aiKeys };

/**
 * List the user's AI conversations for the active org. Server caps
 * the page size to 20 and orders by most-recently-updated.
 */
export function useAiConversations(orgId: string | null | undefined) {
  const http = useHttpClient();
  const enabled = Boolean(orgId);
  const query = useQuery({
    queryKey: orgId ? aiKeys.conversations(orgId) : ['ai', 'idle', 'conversations'],
    enabled,
    queryFn: async () => {
      const { data } = await http.get(`/orgs/${orgId}/ai/conversations`);
      return AiConversationListResponseSchema.parse(data);
    },
  });
  return {
    conversations: (query.data?.items ?? []) as AiConversationSummary[],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Load a single conversation, including its persisted UIMessage[]. */
export function useAiConversation(
  orgId: string | null | undefined,
  conversationId: string | null | undefined,
) {
  const http = useHttpClient();
  const enabled = Boolean(orgId && conversationId);
  const query = useQuery({
    queryKey:
      orgId && conversationId
        ? aiKeys.conversation(orgId, conversationId)
        : ['ai', 'idle', 'conversation'],
    enabled,
    queryFn: async () => {
      const { data } = await http.get(
        `/orgs/${orgId}/ai/conversations/${conversationId}`,
      );
      return AiConversationDetailSchema.parse(data) as AiConversationDetail;
    },
  });
  return {
    conversation: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
}

/** Delete a conversation and invalidate the inbox cache. */
export function useDeleteAiConversation(orgId: string | null | undefined) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (conversationId: string) => {
      if (!orgId) throw new Error('No active organization');
      await http.delete(`/orgs/${orgId}/ai/conversations/${conversationId}`);
    },
    onSuccess: () => {
      if (orgId) {
        void queryClient.invalidateQueries({ queryKey: aiKeys.conversations(orgId) });
      }
    },
  });
  return {
    deleteConversation: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
